/**
 * Login Page
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Button, Typography, CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  window.location.hostname === 'localhost'
    ? 'http://localhost:8000/api'
    : `${window.location.protocol}//${window.location.host}/api`
);

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    const canva = searchParams.get('canva');
    const errorParam = searchParams.get('error');

    if (token && canva === 'connected') {
      sessionStorage.removeItem('canva_auth_pending');
      loginWithToken(token);
      return;
    }

    if (errorParam) {
      sessionStorage.removeItem('canva_auth_pending');
      window.history.replaceState({}, '', '/login');
      navigate('/switch-account');
      return;
    }

    // User came back from Canva without completing auth (e.g. "client ID invalid" → back button)
    if (sessionStorage.getItem('canva_auth_pending')) {
      sessionStorage.removeItem('canva_auth_pending');
      navigate('/switch-account');
    }
  }, [searchParams, loginWithToken, navigate]);

  const handleCanvaLogin = () => {
    setLoading(true);
    sessionStorage.setItem('canva_auth_pending', '1');
    window.location.href = `${API_BASE_URL}/auth/canva/authorize`;
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #424242 0%, #757575 50%, #D32F2F 100%)',
    }}>
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 5, borderRadius: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
              Job Posting Automation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              MAS Holdings - Automated Design Generation
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            onClick={handleCanvaLogin}
            sx={{
              mt: 2, mb: 2, py: 1.8,
              fontSize: '1.1rem', fontWeight: 600,
              background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #B71C1C 0%, #7f0000 100%)' },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Login with Canva'}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Restricted to <strong>MAS Holdings Canva accounts</strong> only.
          </Typography>
        </Paper>

        <Typography variant="body2" align="center" sx={{ mt: 3, color: 'white', opacity: 0.9 }}>
          2026 MAS Holdings. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
